import { EventType } from '@models/audio-message';
import {
  ConversationPacing,
  VoiceInputMode,
} from "@models/conversation-settings";

export interface AudioMessageStream {
  sessionId: string;
  audio: ArrayBufferLike | null;
  photoId: string | null;
  eventType: EventType | null;
  conversationPacing?: ConversationPacing;
  /** When true, server forwards user PCM during an active assistant turn (VAD barge-in). */
  vadBargeInEnabled?: boolean;
  /** Drives server StreamState suppression for push-to-talk streaming turns. */
  voiceInputMode?: VoiceInputMode;
  /**
   * With push_to_talk + UserInput, tells the server to close the active PCM stream
   * after explicit end talk (PTT release), so STT/LLM finalize on user intent.
   */
  endUserUtterance?: boolean;
  /**
   * With push_to_talk + UserInput, tells the server to abort the active PCM stream
   * and discard any buffered transcript (e.g. gallery slide while PTT was
   * recording). Unlike `endUserUtterance` it must NOT produce an LLM turn.
   */
  abortUserUtterance?: boolean;
  /** Monotonic per tab; server echoes so stale parallel streams can be ignored. */
  streamSeq?: number;
}

/**
 * Companion WS payload: user released PTT (end talk). Server closes the active PCM
 * stream so Transcribe + two-phase buffer finalize on explicit intent, not AWS idle.
 */
export function pushToTalkEndStreamMessage(params: {
  sessionId: string;
  photoId?: string | null;
  conversationPacing?: ConversationPacing;
  vadBargeInEnabled?: boolean;
  voiceInputMode: VoiceInputMode;
}): AudioMessageStream {
  return {
    sessionId: params.sessionId,
    audio: null,
    photoId: params.photoId ?? null,
    eventType: EventType.UserInput,
    conversationPacing: params.conversationPacing,
    vadBargeInEnabled: params.vadBargeInEnabled,
    voiceInputMode: params.voiceInputMode,
    endUserUtterance: true,
  };
}

/**
 * Companion WS payload: user's PTT capture was superseded (e.g. gallery slide
 * while recording). Server aborts the active PCM stream and drops any buffered
 * transcript so no out-of-context LLM reply races the new intro.
 */
export function pushToTalkAbortStreamMessage(params: {
  sessionId: string;
  photoId?: string | null;
  conversationPacing?: ConversationPacing;
  vadBargeInEnabled?: boolean;
  voiceInputMode: VoiceInputMode;
}): AudioMessageStream {
  return {
    sessionId: params.sessionId,
    audio: null,
    photoId: params.photoId ?? null,
    eventType: EventType.UserInput,
    conversationPacing: params.conversationPacing,
    vadBargeInEnabled: params.vadBargeInEnabled,
    voiceInputMode: params.voiceInputMode,
    abortUserUtterance: true,
  };
}
